import { matchPath } from "react-router-dom";
import type { Language } from "@/lib/i18n";
import { PT_PATH_PREFIX, PUBLIC_ROUTE_PATHS } from "@/lib/publicRoutes";

// Public pages exist once per language: English at "/...", Portuguese at "/pt/...".
// The authenticated app (/portfolio, /analysis) has no prefix; it follows the
// language saved in user_settings instead.

export const SITE_ORIGIN = "https://oitava.app";

const PATH_PREFIX: Record<Language, string> = {
  en: "",
  "pt-PT": PT_PATH_PREFIX,
};

function hasPtPrefix(pathname: string): boolean {
  return (
    pathname === PT_PATH_PREFIX || pathname.startsWith(`${PT_PATH_PREFIX}/`)
  );
}

/** The language a URL path is in: "/pt" and "/pt/..." are Portuguese. */
export function languageFromPath(pathname: string): Language {
  return hasPtPrefix(pathname) ? "pt-PT" : "en";
}

/** "/pt/pricing" gives "/pricing", "/pt" gives "/". Other paths are returned as is. */
export function stripLanguagePrefix(pathname: string): string {
  if (!hasPtPrefix(pathname)) return pathname;
  return pathname.slice(PT_PATH_PREFIX.length) || "/";
}

/** True for a public page (prefixed or not), false for the app and unknown paths. */
export function isPublicPath(pathname: string): boolean {
  const path = stripLanguagePrefix(pathname);
  return PUBLIC_ROUTE_PATHS.some((pattern) =>
    matchPath({ path: pattern, end: true }, path),
  );
}

/**
 * The same page in the given language. Public paths get the language prefix,
 * anything else (app routes, external links) comes back unchanged.
 * Keeps the query string and hash.
 */
export function localizePath(to: string, language: Language): string {
  if (!to.startsWith("/")) return to;
  const suffixStart = to.search(/[?#]/);
  const pathname = suffixStart === -1 ? to : to.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? "" : to.slice(suffixStart);
  if (!isPublicPath(pathname)) return to;

  const bare = stripLanguagePrefix(pathname);
  const prefix = PATH_PREFIX[language];
  const localized = prefix && bare === "/" ? prefix : `${prefix}${bare}`;
  return `${localized}${suffix}`;
}

/** Absolute URL of a public page in the given language, for canonical and hreflang tags. */
export function absoluteUrl(pathname: string, language: Language): string {
  return `${SITE_ORIGIN}${localizePath(pathname, language)}`;
}
